/**
 * Helper utility to map ServiceType from notification metadata to route paths
 * and build navigation URLs for service request detail pages
 */

export interface ServiceRouteMapping {
  route: string;
  queryParamName: string;
}

/**
 * Map ServiceType values to their corresponding route paths and query parameter names
 */
const SERVICE_TYPE_ROUTE_MAP: Record<string, ServiceRouteMapping> = {
  'Leave Request': {
    route: '/services/leave-request',
    queryParamName: 'requestId'
  },
  'Rest Request': {
    route: '/services/dayoff-requests',
    queryParamName: 'requestId'
  },
  'Transfer Request': {
    route: '/services/transfer-requests',
    queryParamName: 'requestId'
  },
  'Transfer Appeal Request': {
    route: '/services/transfer-appeal-request',
    queryParamName: 'requestId'
  },
  'Complaint': {
    route: '/services/complaints-request',
    queryParamName: 'requestId'
  },
  'Grievance Request': {
    route: '/services/grievance-request',
    queryParamName: 'requestId'
  },
  'Career Progression Request': {
    route: '/services/career-progression-request',
    queryParamName: 'requestId'
  },
  'Bank Salary Card Request': {
    route: '/services/bank-salary-card-request',
    queryParamName: 'requestId'
  },
  'Income Certificate Request': {
    route: '/services/income-certificate-request',
    queryParamName: 'requestId'
  },
  'Certification Request': {
    route: '/services/certificate-requests',
    queryParamName: 'requestId'
  },
  'Certificate Request': {
    route: '/services/certificate-requests',
    queryParamName: 'requestId'
  },
  'Weapon License Request': {
    route: '/services/weapon-license',
    queryParamName: 'requestId'
  },
  'Prosecution Transportation Book Service': {
    route: '/services/prosecution-travel-book-request',
    queryParamName: 'requestId'
  },
  'Prosecution Transfer Service': {
    route: '/services/prosecution-transfer-request',
    queryParamName: 'requestId'
  },
  'External Correspondence Request': {
    route: '/services/external-correspondence-request',
    queryParamName: 'requestId'
  },
  'User Update Request': {
    route: '/services/data-modification',
    queryParamName: 'requestId'
  },
  'Data Modification Request': {
    route: '/services/data-modification',
    queryParamName: 'requestId'
  },
  'Salary Breakdown Request': {
    route: '/services/salary-slip-request',
    queryParamName: 'requestId'
  }
};

/**
 * Build navigation URL from notification metadata
 * @param metadata - Notification metadata containing ServiceType and RequestId
 * @param fallbackLinkUrl - Fallback link URL if metadata is not available
 * @returns Navigation URL string or null if cannot build route
 */
export function buildNotificationRoute(
  metadata?: { ServiceType?: string; RequestId?: string },
  fallbackLinkUrl?: string
): string | null {
  // If no metadata, use fallback linkUrl
  if (!metadata || !metadata.ServiceType) {
    return fallbackLinkUrl || null;
  }

  // Get route mapping for service type
  const routeMapping = SERVICE_TYPE_ROUTE_MAP[metadata.ServiceType];
  
  if (!routeMapping) {
    console.warn(`[Notification Routing] Unknown ServiceType: ${metadata.ServiceType}`);
    return fallbackLinkUrl || null;
  }

  // If no RequestId, return base route only
  if (!metadata.RequestId) {
    return routeMapping.route;
  }

  // Build route with query parameter
  return `${routeMapping.route}?${routeMapping.queryParamName}=${encodeURIComponent(metadata.RequestId)}`;
}

/**
 * Get service route mapping for a given ServiceType
 * @param serviceType - Service type string
 * @returns Route mapping or null if not found
 */
export function getServiceRouteMapping(serviceType?: string): ServiceRouteMapping | null {
  if (!serviceType) {
    return null;
  }
  return SERVICE_TYPE_ROUTE_MAP[serviceType] || null;
}

/**
 * Check if a ServiceType is supported
 * @param serviceType - Service type string
 * @returns True if service type is supported
 */
export function isServiceTypeSupported(serviceType?: string): boolean {
  if (!serviceType) {
    return false;
  }
  return serviceType in SERVICE_TYPE_ROUTE_MAP;
}

